import * as React from "react";

const DEFAULT_TIMEOUT_MS = 3_000;
const HIGHLIGHT_DURATION_MS = 2_600;
export const REVEAL_TARGET_CLASS_NAME = "reveal-target";

export interface WaitForRevealTargetOptions {
  getElement: () => HTMLElement | null;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface UseRevealTargetOptions {
  targetId: string | null | undefined;
  isReady: boolean;
  getElement: () => HTMLElement | null;
  announcement: string;
  timeoutMs?: number;
  onReveal?: () => void;
  onTimeout?: () => void;
}

export type RevealTargetStatus = "idle" | "waiting" | "revealed" | "not-found";

export function waitForRevealTarget({
  getElement,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  signal,
}: WaitForRevealTargetOptions): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const finish = (element: HTMLElement) => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
      signal?.removeEventListener("abort", handleAbort);
      resolve(element);
    };

    const check = () => {
      const element = getElement();
      if (element) finish(element);
    };

    const handleAbort = () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
      reject(new DOMException("Reveal cancelled", "AbortError"));
    };

    const observer = new MutationObserver(check);
    const element = getElement();
    if (element) {
      finish(element);
      return;
    }

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    observer.observe(document.body, { childList: true, subtree: true });
    signal?.addEventListener("abort", handleAbort, { once: true });
    timeoutId = setTimeout(() => {
      observer.disconnect();
      signal?.removeEventListener("abort", handleAbort);
      reject(new Error("Reveal target not found"));
    }, timeoutMs);
  });
}

export function useRevealTarget({
  targetId,
  isReady,
  getElement,
  announcement,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  onReveal,
  onTimeout,
}: UseRevealTargetOptions) {
  const [status, setStatus] = React.useState<RevealTargetStatus>("idle");
  const [liveAnnouncement, setLiveAnnouncement] = React.useState("");
  const callbacksRef = React.useRef({ announcement, getElement, onReveal, onTimeout });

  React.useEffect(() => {
    callbacksRef.current = { announcement, getElement, onReveal, onTimeout };
  });

  React.useEffect(() => {
    if (!targetId || !isReady) {
      setStatus(targetId ? "waiting" : "idle");
      return;
    }

    const controller = new AbortController();
    setStatus("waiting");

    void waitForRevealTarget({
      getElement: () => callbacksRef.current.getElement(),
      timeoutMs,
      signal: controller.signal,
    })
      .then((element) => {
        if (controller.signal.aborted) return;

        const isReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;
        element.scrollIntoView({
          behavior: isReducedMotion ? "auto" : "smooth",
          block: "center",
          inline: "nearest",
        });
        element.focus({ preventScroll: true });
        element.classList.remove(REVEAL_TARGET_CLASS_NAME);
        void element.offsetWidth;
        element.classList.add(REVEAL_TARGET_CLASS_NAME);
        window.setTimeout(() => {
          element.classList.remove(REVEAL_TARGET_CLASS_NAME);
        }, HIGHLIGHT_DURATION_MS);

        setLiveAnnouncement(callbacksRef.current.announcement);
        setStatus("revealed");
        callbacksRef.current.onReveal?.();
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setStatus("not-found");
        callbacksRef.current.onTimeout?.();
      });

    return () => controller.abort();
  }, [isReady, targetId, timeoutMs]);

  return { liveAnnouncement, status };
}
